import react from 'react';
import "@/translations/i18n"
import rem from '@/utils/rem'
import { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import { useNavigate, useLocation } from '@/lib/router-dom';
import usePage from '@/hooks/use-page/index';
import Portal from '@/components/Portal/index';
import Input from '@/components/Input/index';
import Select from '@/components/Select/index';
import SelectorBox from '@/components/SelectorBox/index';
import Dropdown from '@/components/Dropdown/index';
import Button from '@/components/Button/index';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg'
import useModal from '@/hooks/use-modal/index';
import { useForm } from 'react-hook-form'
import useConfig from '@/hooks/use-config'
import classname from 'classnames';
import api from '@/services/api/config.js'
import Toast2 from '@/components/Toast2'
import { useDispatch, useSelector } from 'react-redux';
import { setDataExpiry } from '@/redux/actions/dataExpiry';
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index';

const maxStoreReceiveDays = 2555

const Page = () => {
	/*=================================================
	  General
	/================================================*/

	const { t } = useTranslation();
	const [pageTitle] = useState(t("dataSetting"))
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Configuration',
				link: ``
			},
		]
	 })

	const [editButton, setEditButton] = useState(true)
	const [changeStoreDays, setChangeStoreDays] = useState(0)
	const [changeReceiveDays, setChangeReceivedDays] = useState(0)

	const dispatch = useDispatch()

	/*=================================================
		Error Modal
	/================================================*/

	const [error, setError] = useState()
	function handleError() {
		setError()
	}

	/*=================================================
		API
	/================================================*/

	const { storeDays, receiveDays, error: dataExpiryError } = useSelector(state => state.dataExpiry)

	useEffect(() => {
		setChangeReceivedDays(receiveDays)
		setChangeStoreDays(storeDays)
	}, [receiveDays, storeDays])


	useEffect(() => {
		if (changeReceiveDays < 0) {
			setChangeReceivedDays(receiveDays)
		}
		else if (changeReceiveDays > maxStoreReceiveDays) {
			setChangeReceivedDays(maxStoreReceiveDays)
			setError("Maximum allowed value is " + maxStoreReceiveDays)
		}

	}, [receiveDays])

	useEffect(() => {
		if (changeStoreDays < 0) {
			setChangeStoreDays(storeDays)
		}
		else if (changeStoreDays > maxStoreReceiveDays) {
			setChangeStoreDays(maxStoreReceiveDays)
			setError("Maximum allowed value is " + maxStoreReceiveDays)
		}

	}, [storeDays])


	useEffect(() => {
		setError(dataExpiryError)
	}, [dataExpiryError])

	/*=================================================
		API
	/================================================*/

	const { visible: visibleEdit, show: showEdit, hide: hideEdit } = useModal()
	const { visible: visibleSave, show: showSave, hide: hideSave } = useModal()

	/*=================================================
		Function
	/================================================*/
	function handleIncrement(value, setValue) {
		const parsed = parseInt(value)
		if (isNaN(parsed)) { return 0 }

		setValue(parsed + 1)
	}

	function handleDecrement(value, setValue) {
		const parsed = parseInt(value)
		if (isNaN(parsed)) { return 0 }

		setValue(parsed - 1)
	}

	async function handleSave() {

		const payload = {
			"uploaded_attachment_exp": parseInt(changeStoreDays),
			"received_attachment_exp": parseInt(changeReceiveDays)
		}

		const [error] = await api.saveAttachment(payload);

		dispatch(setDataExpiry({ storeDays: changeStoreDays, receiveDays: changeReceiveDays }))
		hideSave()
		setEditButton(true)

		if (error) {
			return setError(error?.error?.message || "Failed to save data setting config")
		}

		window.location.replace(window.location.href)
	}

	/*=================================================
		Classname - Check
	/================================================*/

	const checkEdit = classname({
		"input-type-number": true,
		"input-type-number--disabled": editButton
	})

	const checkEditButtonForDecrement = classname({
		"input-type-number__decrement": true,
		"input-type-number__decrement--disabled": editButton
	})
	const checkEditButtonForIncrement = classname({
		"input-type-number__increment": true,
		"input-type-number__increment--disabled": editButton
	})

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
				<div className="title-header__right">
					{
						editButton && (
							<Button ghost full maxWidth={80} text={t("edit")}
								submit={() => showEdit()}
							/>
						)
					}
				</div>
			</div>

			<hr />

			<div className="overview">
				<div className="overview__content">
					<div className="data-settings__cols">
						<div className="data-settings__col">
							<div className="data-settings__title">
								Data and Attachment Storage
							</div>
							<div className="data-settings__field-cols">
								<div className="data-settings__field-col">
									<div className="data-settings__field-left">
										Store uploaded data in my pitstop for
									</div>
									<div className="data-settings__field-right">
										<div className={checkEdit}>
											<span className={checkEditButtonForDecrement} onClick={() => {
												handleDecrement(changeStoreDays, setChangeStoreDays)
											}}>-</span>
											<input name="" type="number" disabled={editButton} value={changeStoreDays} onChange={(e) => setChangeStoreDays(e.target.value)} />

											<span className={checkEditButtonForIncrement} onClick={() => {
												handleIncrement(changeStoreDays, setChangeStoreDays)
											}}>+</span>
										</div>
										days
									</div>
								</div>
								<div className="data-settings__field-col">
									<div className="data-settings__field-left">
										Store received data in my pitstop for
									</div>
									<div className="data-settings__field-right">
										<div className={checkEdit}>
											<span className={checkEditButtonForDecrement} onClick={() => {
												handleDecrement(changeReceiveDays, setChangeReceivedDays)
											}}>-</span>
											<input name="" type="number" disabled={editButton} value={changeReceiveDays} onChange={(e) => setChangeReceivedDays(e.target.value)} />

											<span className={checkEditButtonForIncrement} onClick={() => {
												handleIncrement(changeReceiveDays, setChangeReceivedDays)
											}}>+</span>
										</div>
										days
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>


			{
				!editButton && (
					<div className="DS__layout__footer">
						<div className="layout-default__sticky-actions">
							<div className="layout-default__sticky-actions__left">
								<Button text={t("cancel")}
									sizing='Medium'
									full noborder maxWidth='154'
									submit={() => {
										window.location.reload(false)
									}}
								/>
							</div>
							<div className="layout-default__sticky-actions__right">
								<Button text={t("save")}
									sizing='Medium' full round maxWidth='154'
									submit={() => {
										if (changeReceiveDays === '' || changeStoreDays === '') {
											return setError("Please input the number of day for storing data")
										}

										if (changeReceiveDays <= 0 || changeStoreDays <= 0) {
											return setError("Minimum allowed value is 1 ")
										}
										if (changeReceiveDays > maxStoreReceiveDays || changeStoreDays > maxStoreReceiveDays) {
											return setError("Maximum allowed value is " + maxStoreReceiveDays)
										}
										showSave()
									}}
								/>
							</div>
						</div>
					</div>
				)
			}


			{/* Edit Modal */}

			<Modal visible={visibleEdit} variant='medium' close={hideEdit}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Edit Data Settings
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(16), textAlign: 'center' }}>
								Are you sure you want to save your changes? <br />
								Changes will only be applied from the next day.
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {
									hideEdit()
								}}
							/>
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button text="Confirm"
								id="configuration-data-settings-edit-button"
								submit={() => {
									setEditButton(false)
									hideEdit()
								}} />

						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			{/* Save Modal */}

			<Modal visible={visibleSave} variant='medium'  close={hideSave}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Save Data Settings
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(16), textAlign: 'center' }}>Are you sure you want to save your changes?</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {
									hideSave()
								}}
							/>
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button text="Confirm"
								id="configuration-data-settings-save-button"
								submit={() => {
									// setSelectedKeyManagement(selectedTempKey);
									// hideKeyManagementModal()
									handleSave()
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
			<Toast2 show={error} errorMessage={error} close={() => { handleError() }} />
		</>
	)
}

export default Page;
